import React, { useEffect, useState } from "react";
import { AirplaneTilt, ArrowRight, Buildings } from "@phosphor-icons/react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Disclaimer from "../components/Disclaimer";

const REGIONS = [
  { v: "EU", label: "European Union" },
  { v: "US", label: "United States" },
  { v: "CA", label: "Canada" },
  { v: "MIXED", label: "Mixed / Other" },
];

export default function Signup() {
  const { setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const googleEmail = sp.get("email") || "";
  const googleName = sp.get("name") || "";
  const fromGoogle = sp.get("source") === "google";
  const [form, setForm] = useState({
    tenant_name: "",
    tenant_slug: "",
    admin_email: googleEmail,
    admin_name: googleName,
    admin_password: "",
    operating_region: "EU",
  });
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);

  const tryDemo = async () => {
    setErr(""); setDemoBusy(true);
    try {
      const r = await api.post("/tenants/signup-demo", {});
      setUser(r.data.user);
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Demo workspace failed to load");
      setDemoBusy(false);
    }
  };

  // Auto-suggest workspace name + slug from the Google name when no other
  // edit has happened yet.
  useEffect(() => {
    if (fromGoogle && googleName && !form.tenant_name) {
      const guess = `${googleName.split(" ")[0]}'s Workspace`;
      setForm((f) => ({
        ...f,
        tenant_name: guess,
        tenant_slug: f.tenant_slug || autoSlug(guess),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  function autoSlug(name) {
    return name
      .toLowerCase()
      .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 30);
  }

  const onNameChange = (e) => {
    const name = e.target.value;
    setForm((f) => ({
      ...f,
      tenant_name: name,
      tenant_slug: f.tenant_slug || autoSlug(name),
    }));
  };

  const passwordOk =
    form.admin_password.length >= 10 &&
    /[A-Za-z]/.test(form.admin_password) &&
    /\d/.test(form.admin_password);
  const matches = confirm === form.admin_password && confirm.length > 0;
  const canSubmit = !!form.tenant_name && form.tenant_slug.length >= 3 &&
    !!form.admin_email && !!form.admin_name && passwordOk && matches && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/tenants/signup", form);
      setUser(r.data.user);
      await refresh();
      navigate("/", { replace: true });
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      const errors = e2.response?.data?.errors;
      setErr(
        Array.isArray(errors) && errors.length
          ? errors.map((x) => x.msg).join("; ")
          : (detail || "Signup failed")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100 grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-zinc-900 relative overflow-hidden bg-gradient-to-br from-[#0A0A0A] via-[#0F0F0F] to-[#1A0F00]">
        <div className="flex items-center gap-3">
          <AirplaneTilt size={28} weight="duotone" className="amber" />
          <div className="font-mono text-sm tracking-wider">OWNERCAM</div>
        </div>
        <div className="relative">
          <Buildings size={56} weight="duotone" className="amber mb-6" />
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">New workspace · 90-day free trial</div>
          <h1 className="text-4xl xl:text-5xl font-semibold tracking-tight mb-5 leading-[1.05]">
            Start tracking your<br /><span className="amber">aircraft maintenance</span> in 30 seconds.
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-md">
            Each workspace gets its own private aircraft, maintenance items, parts,
            ADs, documents and audit trail — isolated from every other workspace.
            Cost sharing is available in the Pro plan.
          </p>
        </div>
        <div className="text-xs text-zinc-500 font-mono">
          90 days free · Auto-renews after trial · Cancel anytime
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-10">
        <form className="w-full max-w-md space-y-5" onSubmit={submit} data-testid="signup-form">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <AirplaneTilt size={22} weight="duotone" className="amber" />
            <div className="font-mono text-xs tracking-wider">OWNERCAM</div>
          </div>

          {fromGoogle && (
            <div className="p-4 border border-amber/40 bg-amber/5 text-xs text-zinc-300 font-mono" data-testid="signup-google-banner">
              <div className="amber uppercase tracking-[0.2em] text-[10px] mb-1">Continuing as {googleName || googleEmail}</div>
              <div className="text-zinc-400 normal-case">Your Google account isn&apos;t linked to a workspace yet — finish workspace setup to get started. Email locked to <span className="text-amber">{googleEmail}</span>.</div>
            </div>
          )}

          {!fromGoogle && (
            <button
              type="button"
              onClick={tryDemo}
              disabled={demoBusy || busy}
              className="w-full h-12 border border-amber/60 bg-amber/5 text-amber font-medium tracking-wide hover:bg-amber/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors text-sm"
              data-testid="signup-demo-btn"
            >
              {demoBusy ? "Spinning up demo workspace…" : (
                <>Try with sample data <ArrowRight size={14} weight="bold" /></>
              )}
            </button>
          )}

          {!fromGoogle && (
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-mono">
              <div className="h-px flex-1 bg-zinc-900" />
              or sign up for real
              <div className="h-px flex-1 bg-zinc-900" />
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Workspace name</div>
            <input
              value={form.tenant_name}
              onChange={onNameChange}
              placeholder="Cluj Aero Group"
              autoFocus
              className="w-full h-11 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm focus:outline-none focus:border-amber"
              data-testid="signup-name"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">URL slug</div>
            <div className="flex items-center bg-[#0F0F0F] border border-zinc-800 px-3 h-11">
              <span className="text-zinc-600 text-xs font-mono mr-1">.../t/</span>
              <input
                value={form.tenant_slug}
                onChange={set("tenant_slug")}
                placeholder="cluj-aero"
                className="flex-1 bg-transparent text-sm font-mono focus:outline-none"
                data-testid="signup-slug"
              />
            </div>
            <div className="text-[10px] text-zinc-600 mt-1 font-mono">
              lowercase letters, digits, dashes — 3 to 32 chars
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Region</div>
              <select
                value={form.operating_region}
                onChange={set("operating_region")}
                className="w-full h-11 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm focus:outline-none focus:border-amber"
                data-testid="signup-region"
              >
                {REGIONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1">Your name</div>
              <input
                value={form.admin_name}
                onChange={set("admin_name")}
                placeholder="Andrei P."
                className="w-full h-11 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm focus:outline-none focus:border-amber"
                data-testid="signup-admin-name"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-zinc-900">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-3 mt-3">Admin account</div>
            <input
              type="email"
              value={form.admin_email}
              onChange={set("admin_email")}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={fromGoogle}
              className="w-full h-11 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm focus:outline-none focus:border-amber mb-3 disabled:text-zinc-500 disabled:cursor-not-allowed"
              data-testid="signup-email"
            />
            <input
              type="password"
              value={form.admin_password}
              onChange={set("admin_password")}
              placeholder="Password (min 10 chars, letters + digits)"
              autoComplete="new-password"
              className="w-full h-11 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm focus:outline-none focus:border-amber mb-3"
              data-testid="signup-password"
            />
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm password"
              autoComplete="new-password"
              className="w-full h-11 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm focus:outline-none focus:border-amber"
              data-testid="signup-confirm"
            />
            {confirm && !matches && (
              <div className="text-[11px] text-red-400 mt-1 font-mono">Passwords don&apos;t match.</div>
            )}
          </div>

          {err && (
            <div className="p-3 border border-red-900/60 bg-red-950/20 text-xs text-red-300 font-mono" data-testid="signup-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full h-12 bg-amber text-black font-medium tracking-wide hover:bg-amber/90 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            data-testid="signup-submit"
          >
            {busy ? "Creating workspace…" : (<>Start 90-day free trial <ArrowRight size={16} weight="bold" /></>)}
          </button>

          <Disclaimer variant="compact" />

          <div className="text-center text-xs text-zinc-500">
            Already have a workspace?{" "}
            <Link to="/login" className="amber hover:underline" data-testid="signup-to-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
